import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { MatSelect } from '@angular/material/select';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { VirtualScrollerComponent } from 'ngx-virtual-scroller';
import differenceBy from 'lodash-es/differenceBy';
import _get from 'lodash-es/get';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

interface Collection {
  id: number;
  name: string;
  hide?: boolean;
}

@Component({
    selector: 'caiman-filter-input',
    templateUrl: './filter-input.component.html',
    styleUrls: ['./filter-input.component.scss'],
    imports: [MatFormFieldModule, MatInputModule, ReactiveFormsModule, MatIconModule, NgIf, MatTooltipModule, MatCheckboxModule]
})

export class FilterInputComponent implements OnInit, OnChanges, OnDestroy {
  ngUnsubscribe: Subject<void> = new Subject<void>();

  /** collection that should be filtered */
  @Input() set collection(collection: Collection[]) {
    if (!this._initialCollection) {
      this._initialCollection = [...collection];
    }
  }

  get collection(): Collection[] {
    return this._initialCollection;
  }
  get selectAllEnable(): boolean {
    return this.filteredCollectionLength < this.selectAllCount; // show select only if total item amount < 500
  }
  /** already selected values, use only with mat-select */
  @Input() selected: Collection[] = [];
  /** array item property to filter by */
  @Input() prop: string;
  /** search input placeholder */
  @Input() placeholder: string;
  /** mat select */
  @Input() matSelect: MatSelect;
  /** virtual scroll */
  @Input() virtualScroll: VirtualScrollerComponent;
  /** filter by id flag */
  @Input() isFilterById: boolean;
  /** select all flag */
  @Input() isSelectAll?: boolean = false;
  /** show selected only flag */
  @Input() showSelectedOnly?: boolean = false;
  /** event that emit filtered collection */
  @Output() collectionChange = new EventEmitter<Collection[]>();
  /** input element ref */
  @ViewChild('input', { static: false }) input: ElementRef;
  /** Search term form control*/
  termControl = new UntypedFormControl('');
  /** show selected only flag */
  showSelected: boolean;
  /** Filtered collection length */
  filteredCollectionLength: number;
  /** max select all count per click */
  selectAllCount = 500;
  /** cached collection */
  private _initialCollection: { id: number; name: string; hide?: boolean }[];

  constructor() { }

  ngOnInit() {
    this.filteredCollectionLength = this.collection.length;
    // Re-run the filter to clear any stale hide flags on the shared collection before the panel renders
    this.getFilteredResult();
    // overwrite matSelect function with empty function to avoid option list scroll on select
    if (this.matSelect) {
      this.matSelect['_scrollActiveOptionIntoView'] = () => { };
    }
    // DOM manipulation, don't work without timeout even in ngAfterViewInit
    setTimeout(() => {
      // focus search input by default
      this.input.nativeElement.focus();

      if (this.matSelect && this.matSelect.panel) {
        const panelEl = this.matSelect.panel.nativeElement as HTMLElement;
        // set width for having always the same select panel width when filtering
        panelEl.style.width = `${panelEl.clientWidth}px`;

        // Align the overlay pane with the mat-select field.
        const pane = panelEl.closest('.cdk-overlay-pane') as HTMLElement;
        const hostEl = this.matSelect['_elementRef'].nativeElement as HTMLElement;
        if (pane) {
          requestAnimationFrame(() => {
            const paneRect = pane.getBoundingClientRect();
            const hostRect = hostEl.getBoundingClientRect();
            const dx = Math.round(hostRect.left - paneRect.left);
            // Snap vertically to the field: below the field -> top to host bottom; above -> bottom to host top.
            const dy = paneRect.top >= hostRect.top
              ? Math.round(hostRect.bottom - paneRect.top)
              : Math.round(hostRect.top - paneRect.bottom);
            pane.style.transform = `translate(${dx}px, ${dy}px)`;
          });
        }

        // set height for virtual scroll
        if (this.virtualScroll) {
          const height = getComputedStyle(panelEl).maxHeight.slice(0, -2);
          this.virtualScroll['element'].nativeElement.style.height = `${+height - 101}px`;
        }
      }
    });
  }

  ngOnChanges(changes) {
    if (changes && changes.selected && !changes.selected.firstChange) {
      // reset show selected flag if user unselect all options
      if (!changes.selected.currentValue.length) {
        this.showSelected = this.isSelectAll = false;
        return this.emitCollectionChange();
      }
      // emit modified collection on selected value changes for filtering selected
      setTimeout(() => {
        this.emitCollectionChange();
        this.setSelectAll();
      });
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.showSelected = false;
  }

  /**
   * On select all
   *
   * @param event
   */
  onSelectAll(event: { checked: boolean }): void {
    const visibleItems = this.getFilteredResult().filter(elem => !elem.hide);
    this.matSelect.ngControl.control.patchValue(
      event.checked ?
        visibleItems.concat(differenceBy(this.selected, visibleItems, this.prop)) :
        differenceBy(this.selected, visibleItems, this.prop)
    );
    setTimeout(() => this.setSelectAll());
  }

  /**
   * Sent filtered data on checkbox change
   *
   * @param event
   */
  onCheckboxChange(event: { checked: boolean }): void {
    this.showSelected = event.checked;
    this.setSelectAll();
    this.emitCollectionChange();
  }

  /**
   * Sent filtered data on input key up
   */
  onSearchTermChanged(): void {
    this.setSelectAll();
    this.emitCollectionChange();
  }

  /**
   * Clear search term input
   */
  clearTerm(): void {
    this.termControl.patchValue('');
    this.setSelectAll();
    this.emitCollectionChange();
  }

  /**
   * Set select all value
   */
  private setSelectAll(): void {
    const filteredCollection = this.getFilteredResult().filter(elem => !elem.hide);
    this.filteredCollectionLength = filteredCollection.length;
    this.isSelectAll = Boolean(filteredCollection.length) && !differenceBy(
      filteredCollection,
      this.matSelect && this.matSelect.ngControl ? this.matSelect.ngControl.control.value : '',
      this.prop
    ).length;
  }

  /**
   * Emit data changes
   */
  private emitCollectionChange(): void {
    this.collectionChange.emit(this.getFilteredResult());
  }


  /**
   * Get filtered result
   */
  private getFilteredResult(): Collection[] {
    const collection = this.showSelected && this.selected.length ? this.selected : this._initialCollection || [];
    const term = (this.termControl.value || '').toLowerCase();
    const result = collection.map(elem => {
      // Null-safe: some items may not have a value at `prop`.
      // Coalescing to '' avoids `undefined.toLowerCase()` throwing errors
      const idMatches = elem.id.toString().toLowerCase().includes(term);
      const propMatches = String(_get(elem, this.prop) || '').toLowerCase().includes(term);
      elem.hide = this.isFilterById ?
        !idMatches && !!this.prop && !propMatches :
        !!this.prop && !propMatches;
      return elem;
    });

    return result;
  }
}
